/**
 * Composes the context-authored layer with the three request-supplied
 * config layers (view/user/session) for ONE level, returning the
 * `ComposedLevelFilters` a `LevelFilterPopoverOrganism` renders and a
 * `useLevelFilters` request sends.
 *
 * `runtime` excludes `context` — the frontend never echoes the
 * context-authored layer back in `runtimeFilters`; the backend already
 * applies it (`level.filters` on the stored context), and echoing it would
 * double-apply the clause and make the provenance map lie about who owns
 * the constraint. `display` includes it so the UI can render it as a locked
 * chip (see `docs/design/context-view-column-filters.md` §4.1).
 *
 * When a `link` layer is supplied (EAINT-793 — an active `?f=` shared filter
 * link), it REPLACES `view`/`user`/`session` in both `runtime` and
 * `provenance` rather than composing alongside them: a recipient's own
 * layers must not narrow a sender's shared board, and the recipient's `view`
 * layer applying on top of a `runtime` already built from the sender's own
 * `view` clauses would double-apply them. `context` is never suppressed —
 * it is not the user's to bypass and doing so would be a widening.
 *
 * Every clause carries its EAINT-768 `id` when one exists. Clauses stored
 * before ids existed — or minted on the execute response path, which does
 * not mint ids (see spec gap 9) — lack one; those get a deterministic
 * `${layer}:${lane}:${index}` key so the provenance map is keyed on
 * something stable rather than colliding on `undefined`.
 */
import type { ComposedLevelFilters, FilterLayer, FilterProvenanceEntry } from 'types/contextView';
import type { LocalizedString } from 'types/context';
import type { ArcFilter, FilterExpression, FilterGroup, PerspectiveFilter, RelationshipFilter, UnifiedFilters } from 'types/filters';
import { concatUnifiedFilters, hasAnyFilterLane } from '@center-platform/spheres/filters';

const LANES = ['propertyFilters', 'perspectiveFilters', 'relationshipFilters', 'arcFilters', 'filterGroups'] as const;
type Lane = (typeof LANES)[number];

/** One clause from any lane. Every member carries an optional `id`. */
type LaneClause = FilterExpression | PerspectiveFilter | RelationshipFilter | ArcFilter | FilterGroup;

/** The property/perspective/relationship a clause constrains, for grouping chips by column. */
function laneTarget(lane: Lane, clause: LaneClause): string | undefined {
  switch (lane) {
    case 'propertyFilters':
      return (clause as FilterExpression).property;
    case 'perspectiveFilters':
      return (clause as PerspectiveFilter).perspectiveSystemName;
    case 'relationshipFilters':
      return (clause as RelationshipFilter).relationshipSystemName;
    case 'arcFilters':
      return (clause as ArcFilter).templateSystemName;
    case 'filterGroups':
      return undefined;
    default:
      return undefined;
  }
}

function collectProvenance(layer: FilterLayer, filters: UnifiedFilters | undefined, provenance: FilterProvenanceEntry[]): void {
  LANES.forEach((lane) => {
    const entries: ReadonlyArray<LaneClause> = filters?.[lane] ?? [];
    entries.forEach((clause, index) => {
      provenance.push({
        filterId: clause.id ?? `${layer}:${lane}:${index}`,
        layer,
        lane,
        target: laneTarget(lane, clause),
      });
    });
  });
}

export interface LevelFilterLayers {
  /** Read-only — the level's stored `filters` (plus segments) on the context definition. */
  context: UnifiedFilters | undefined;
  view: UnifiedFilters | undefined;
  user: UnifiedFilters | undefined;
  session: UnifiedFilters | undefined;
  /**
   * The decoded envelope for this level from an active `?f=` shared filter
   * link (EAINT-793). Presence — not content — is what activates link mode:
   * `undefined` reproduces pre-EAINT-793 composition exactly, while a
   * defined `link` (including `{}`, when the link carries nothing for this
   * particular level) REPLACES `view`/`user`/`session` in `runtime` rather
   * than composing alongside them. A link only suppresses the recipient's
   * own layers, never `context` — see the module doc.
   */
  link?: UnifiedFilters;
}

/**
 * One level's contribution to a view-wide ('*' scope) context tier — Atlas's
 * column popover renders one row per level with a clause on the aspect being
 * filtered, rather than the single locked chip a per-level scope shows.
 */
export interface LevelFilterTierRow {
  levelId: string;
  label: LocalizedString;
  filters: UnifiedFilters;
}

/**
 * Narrows every level's own `level.filters` (the `context` layer) down to
 * clauses targeting one aspect (a property/perspective/relationship/arc-
 * template name, per `laneTarget`), for the view-wide scope's context tier
 * (`specs/unified-filters-v3.md` § Technical Design: "context tier = every
 * level's `level.filters` narrowed to the column aspect, labelled by
 * level"). Only levels with at least one matching clause produce a row —
 * this is a display list, not a per-level default.
 */
export function narrowLevelFiltersToAspect(
  levels: ReadonlyArray<{ id: string; label: LocalizedString; filters?: UnifiedFilters }>,
  aspectTarget: string
): LevelFilterTierRow[] {
  const rows: LevelFilterTierRow[] = [];
  levels.forEach((level) => {
    const narrowed: UnifiedFilters = {};
    LANES.forEach((lane) => {
      const entries = (level.filters?.[lane] as unknown as ReadonlyArray<LaneClause> | undefined) ?? [];
      const matching = entries.filter((clause) => laneTarget(lane, clause) === aspectTarget);
      if (matching.length > 0) {
        (narrowed[lane] as unknown as LaneClause[]) = matching;
      }
    });
    if (hasAnyFilterLane(narrowed)) {
      rows.push({ levelId: level.id, label: level.label, filters: narrowed });
    }
  });
  return rows;
}

export function composeLevelFilters(layers: LevelFilterLayers): ComposedLevelFilters {
  const provenance: FilterProvenanceEntry[] = [];
  collectProvenance('context', layers.context, provenance);

  const linkActive = layers.link !== undefined;
  if (linkActive) {
    collectProvenance('link', layers.link, provenance);
  } else {
    collectProvenance('view', layers.view, provenance);
    collectProvenance('user', layers.user, provenance);
    collectProvenance('session', layers.session, provenance);
  }

  const runtime = linkActive
    ? (layers.link as UnifiedFilters)
    : concatUnifiedFilters(concatUnifiedFilters(layers.view, layers.user), layers.session) ?? {};
  const display = concatUnifiedFilters(layers.context, runtime) ?? {};

  return { runtime, display, provenance };
}

/**
 * The inverse of composition: splits a `ComposedLevelFilters` back into one
 * `UnifiedFilters` per layer, for read-only display surfaces
 * (`FilterProvenanceGroupMolecule`, EAINT-781) that need to render "what did
 * THIS layer contribute" without the caller separately threading the four
 * raw layer inputs alongside the composed result.
 *
 * Relies on an ordering invariant `composeLevelFilters` establishes above:
 * `display[lane]` is the concatenation `context[lane] ++ view[lane] ++
 * user[lane] ++ session[lane]`, and `provenance` — built by calling
 * `collectProvenance` once per layer in that same order — lists every
 * lane's entries in that same relative order within the lane. So
 * `provenance.filter(p => p.lane === lane)` is index-aligned with
 * `display[lane]`, and zipping the two recovers each clause's layer without
 * needing the raw per-layer arrays. If `composeLevelFilters`'s layer or lane
 * ordering ever changes, this function must change with it — they are two
 * views of one invariant, kept in this file together on purpose.
 */
export function groupComposedFiltersByLayer(
  composed: ComposedLevelFilters
): Record<FilterLayer, UnifiedFilters> {
  const result: Record<FilterLayer, UnifiedFilters> = { context: {}, view: {}, user: {}, session: {}, link: {} };
  LANES.forEach((lane) => {
    const entries = (composed.display[lane] as unknown as Array<Record<string, unknown>> | undefined) ?? [];
    const laneProvenance = composed.provenance.filter((p) => p.lane === lane);
    entries.forEach((entry, index) => {
      const layer = laneProvenance[index]?.layer;
      if (!layer) return;
      const bucket = result[layer];
      const existing = (bucket[lane] as unknown[] | undefined) ?? [];
      (bucket[lane] as unknown[]) = [...existing, entry];
    });
  });
  return result;
}
